// client/src/features/owner-tenancy/search.ts — SCR-OAT-001's URL state · OAT-003 (04 §9.2.3).
//
// §9.2.3 is NORMATIVE and applies here even though 04 §8.15 calls this a "single-screen module":
// which rental is open, which tab is showing, which month the summary answers for and every
// filter of the personal ledger are all Zod-validated search params, so a personal ledger is
// shareable, survives a refresh, and Back undoes a filter rather than leaving the section.
//
// ONE ROUTE, and the tabs are params rather than child routes (D-1190). §6.5's DETAIL-TABS
// prints "route-linked tabs" and §8.15 gives this module exactly one URL — the two are only
// compatible if the link is a search param, which is what `?tab=` is.
//
// The ledger's filter names are the endpoint's own — `categoryId`, `status`, `from`, `to`, `q` —
// so the URL and the request say the same words, as `features/expenses/search.ts` does. Two of
// the register's filters are deliberately NOT here: `propertyId`, because a personal cost has no
// property (03 §16.3's XOR), and `isCapex`, because the personal footer's CapEx figure is a
// constant zero (D-1063) and a filter whose result the footer cannot describe is a filter that
// only produces questions.

import { z } from 'zod';

import { EXPENSE_STATUSES } from '@/features/expenses/enums';
import { REPAIR_TICKET_STATUSES, REPAIR_URGENCIES } from '@/features/repairs/enums';
import { DOCUMENT_SOURCE_FILTERS, RENT_STATUS_FILTERS } from '@/features/portal/search';
import { urlBoolean, zUrlBoolean } from '@/lib/url-state';

/** Newest cost first — `ExpensesEndpoints.Sortable`'s own default, spelled so a shared URL carries it. */
export const DEFAULT_PERSONAL_SORT = 'expenseDate:desc';

/**
 * The panels of the DETAIL-TABS body, PORTAL FIRST (D-1730).
 *
 * The first five are the tenant portal's own screens, rendered by the very components `/portal`
 * renders and shown only for a rental that mirrors a contract: a landlord opens this section to
 * see what they owe and what their meters read, not to open their ledger. The last three are the
 * personal side and are the only ones a hand-typed rental has — 04 §8.15's „tenancy card,
 * personal expense ledger + recurring rules, personal monthly summary".
 *
 * „recurring" is OAT-004's: §8.15 always asked for it and D-1191 shipped two tabs because the
 * third had no backend to render (`recurring_expenses.property_id` was NOT NULL). It has one now.
 */
export const OAT_PORTAL_TABS = ['rent', 'meters', 'incidents', 'documents', 'alerts'] as const;

export const OAT_PERSONAL_TABS = ['summary', 'ledger', 'recurring'] as const;

export const OAT_TABS = [...OAT_PORTAL_TABS, ...OAT_PERSONAL_TABS] as const;

export type OatTab = (typeof OAT_TABS)[number];

/** Whether a tab renders a portal surface, and therefore needs a mirrored contract behind it. */
export function isPortalTab(tab: OatTab): boolean {
  return (OAT_PORTAL_TABS as readonly string[]).includes(tab);
}

export const ownerTenancySearchSchema = z.object({
  /** Which rental is open. Absent means "the first the list answers with". */
  tenancy: z.string().trim().optional(),
  /**
   * WITHOUT a default, deliberately, and `activeTab` below is why (D-1730). Which panel a rental
   * opens on depends on what KIND of rental it is — a mirrored one opens on „Chiria mea", a
   * hand-typed one has no such panel and opens on „Rezumat lunar" — and the schema is validating
   * a URL, which does not know which row the picker selected. A default here would have made
   * „the landlord chose the summary" and „nobody chose anything" the same value.
   */
  tab: z.enum(OAT_TABS).optional(),
  /**
   * The rentals list answers with the ACTIVE ones unless asked otherwise
   * (`ListOwnerTenancies(includeInactive)`), and a rental that ended keeps its history — so
   * seeing it is a deliberate switch rather than the default reading.
   *
   * Both spellings, and finding out why is what produced `lib/url-state.ts`: the control writes
   * the string „true" and a shared `?includeInactive=true` arrives as the boolean, because the
   * router parses search values with `JSON.parse` (D-1197).
   */
  includeInactive: zUrlBoolean.optional(),

  // ── the personal monthly summary ───────────────────────────────────────────────────────
  //
  // Both absent asks the server for „luna curentă din România", which is the ONLY correct way
  // to say „this month" here: Europe/Bucharest is the app's business clock (02 §4) and the
  // browser's is whatever the laptop is set to. The month arrives on the payload and the
  // previous/next buttons move it from THERE (D-1192).
  year: z.coerce.number().int().min(2000).max(2100).optional(),
  month: z.coerce.number().int().min(1).max(12).optional(),

  // ── the personal expense ledger ────────────────────────────────────────────────────────
  /** 02 §7.4 — `pageSize` default 25, max 200; the server refuses more rather than clamping. */
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(200).default(25),
  sort: z.string().default(DEFAULT_PERSONAL_SORT),
  q: z.string().trim().optional(),
  categoryId: z.string().trim().optional(),
  status: z.enum(EXPENSE_STATUSES).optional(),
  from: z.string().trim().optional(),
  to: z.string().trim().optional(),

  /**
   * The personal cost form, inline rather than in a modal: `new`, or the id of the cost being
   * edited. A `<dialog>` would fight SCR-EXP-002's sticky action row, which is `fixed` to the
   * viewport so „Salvează" is reachable one-handed at 360 px (04 §11.2) — inside the top layer
   * that row lands over the page, not over the form.
   */
  expense: z.string().trim().optional(),

  // ── the portal panels (D-1730) ─────────────────────────────────────────────────────────
  //
  // PREFIXED, and that is not decoration. `/portal` gives each of its screens a route and so a
  // search space of its own, where `page` and `status` mean one thing each. Here the five share
  // ONE url with the three personal panels, and three different `status` keys — a rent period's,
  // an incident's and an expense's — would be three different enums under one name. The first
  // one to validate would reject the other two and the screen would render a Zod dump.
  //
  // The page numbers could have been shared, since one tab renders at a time, and are not: a
  // landlord who pages their rent to 3, looks at the meters and comes back expects page 3, and a
  // shared key would have reset it to whatever the meters were on.
  /** SCR-PORT-002's status filter and page. */
  rentStatus: z.enum(RENT_STATUS_FILTERS).optional(),
  rentPage: z.coerce.number().int().min(1).optional(),

  /** SCR-PORT-003's page (four meters to a page, D-1562). */
  metersPage: z.coerce.number().int().min(1).optional(),

  /**
   * SCR-PORT-004: the report form, the filters, the page — and `incident`, which is this
   * module's answer to the portal's `/portal/incidents/$incidentId` route. §8.15 gives the module
   * ONE url, so the detail is an overlay param like `expense` above it (D-1190).
   */
  incidentReport: zUrlBoolean.optional(),
  incidentPage: z.coerce.number().int().min(1).optional(),
  incidentQ: z.string().trim().max(200).optional(),
  incidentStatus: z.enum(REPAIR_TICKET_STATUSES).optional().catch(undefined),
  incidentUrgency: z.enum(REPAIR_URGENCIES).optional().catch(undefined),
  incidentActiveOnly: zUrlBoolean.optional(),
  incident: z.string().trim().optional(),

  /** SCR-PORT-005's page (six documents to a page) and its segment (D-2161, D-2166). */
  documentsPage: z.coerce.number().int().min(1).optional(),
  documentsSource: z.enum(DOCUMENT_SOURCE_FILTERS).optional().catch(undefined),

  /** The lease's notification feed: its page, and whether only the unread are in view. */
  alertsPage: z.coerce.number().int().min(1).optional(),
  alertsUnreadOnly: zUrlBoolean.optional(),
});

/**
 * Which panel is on screen: the URL's, when it names one the rental can render, and otherwise the
 * one that rental opens on.
 *
 * A MIRRORED rental opens on „Chiria mea" — a landlord comes here to see what they owe, not to
 * read their own ledger — and a hand-typed one on „Rezumat lunar", which is the only kind of
 * panel it has. The second clause matters as much as the first: a link to `?tab=meters` for a
 * rental with no contract behind it must land somewhere real rather than on five empty panels.
 *
 * @param search the section's search params.
 * @param linked whether the rental in view mirrors a contract.
 */
export function activeTab(search: OwnerTenancySearch, linked: boolean): OatTab {
  if (search.tab !== undefined && (linked || !isPortalTab(search.tab))) return search.tab;

  return linked ? 'rent' : 'summary';
}

/** The `expense` param's one non-id value: „a cost that does not exist yet". */
export const NEW_PERSONAL_COST = 'new';

export type OwnerTenancySearch = z.infer<typeof ownerTenancySearchSchema>;

/** The list endpoint's own parameter, from the URL's tri-state. */
export function includeInactiveFilter(search: OwnerTenancySearch): boolean {
  return urlBoolean(search.includeInactive) ?? false;
}

/** Which ledger filters are engaged — the empty state needs to know why it is empty. */
export function hasActivePersonalFilters(search: OwnerTenancySearch): boolean {
  return (
    (search.q !== undefined && search.q !== '') ||
    search.categoryId !== undefined ||
    search.status !== undefined ||
    search.from !== undefined ||
    search.to !== undefined
  );
}

/**
 * Clearing the ledger's filters. The rental, the tab and the summary's month are NOT filters —
 * they are where the user is — so they survive, and only the five below go back to nothing.
 */
export function clearedPersonalFilters(previous: OwnerTenancySearch): OwnerTenancySearch {
  return {
    tenancy: previous.tenancy,
    tab: previous.tab,
    includeInactive: previous.includeInactive,
    year: previous.year,
    month: previous.month,
    page: 1,
    pageSize: previous.pageSize,
    sort: DEFAULT_PERSONAL_SORT,
  };
}
